'use client'

import { useParams, usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useCatalogBrandsInitialData } from '@/features/catalog-brands/initial-data'
import { useCatalogUrl } from '@/features/catalog-shared/url-context'

import {
	catalogBrandsApiSlice,
	CatalogBrandsProductsData,
	createCatalogBrandsUrlParams
} from '@/entities/catalog-brands'
import { getRegionCookies } from '@/entities/regions'

export const useCatalogBrandsProducts = (filterUrl: string, brandCodeParam?: string, categoryCodeParam?: string) => {
	const pathname = usePathname()
	const searchParams = useSearchParams()
	const params = useParams()
	const urlCtx = useCatalogUrl()
	const resolvedSearch = urlCtx?.search ?? searchParams.toString()
	const searchFromUrl = new URLSearchParams(resolvedSearch)
	const resolvedUrl = urlCtx?.resolvedUrl ?? `${pathname}${resolvedSearch ? `?${resolvedSearch}` : ''}`
	const regionCookies = getRegionCookies()
	const { initialProductsData, initialResolvedUrl } = useCatalogBrandsInitialData()
	const initialResolvedUrlRef = useRef<string>(initialResolvedUrl)
	const canUseInitialForCurrentRoute = initialResolvedUrlRef.current === resolvedUrl

	const queryPage = searchFromUrl.get('page')
	const querySort = searchFromUrl.get('sort')
	const sort = querySort ? querySort.toString() : `popular`
	const page = queryPage ? Number(queryPage) : 1

	// Используем переданные параметры, если они есть, иначе получаем из URL
	const brandCode = brandCodeParam || (params.brand as string) || ''

	// Для маршрута /brand/[brand]/[...category] параметры будут в params.category
	const categoryParams = (params.category as string[]) || []
	const categoryCode =
		categoryCodeParam || (Array.isArray(categoryParams) && categoryParams.length > 0 ? categoryParams[0] : '')

	const [fetchMoreData] = catalogBrandsApiSlice.useLazyGetBrandProductsQuery()
	const [moreData, setMoreData] = useState<CatalogBrandsProductsData | null>(null)

	const urlParams = createCatalogBrandsUrlParams({ sort, page })
	const urlParamsKey = JSON.stringify(urlParams)

	const {
		data: initProductData,
		isFetching: isLazyProductFetching,
		isLoading: isLazyProductLoading
	} = catalogBrandsApiSlice.endpoints.getBrandProducts.useQuery(
		{
			params: urlParams,
			filterUrl,
			brandCode,
			categoryCode,
			regionCookies
		},
		{ skip: !brandCode || (categoryCodeParam === undefined && !categoryCode) }
	)

	const initialProductsSnapshotKey = `${brandCode || ''}-${categoryCode || ''}-${urlParamsKey}-${filterUrl || ''}-${
		initialProductsData?.TOTAL || 0
	}-${initialProductsData?.ITEMS?.length || 0}`
	const initialProductsSnapshotRef = useRef('')
	const initialRequestRef = useRef<{
		urlParamsKey: string
		filterUrl: string
		brandCode: string
		categoryCode: string
	} | null>(null)

	if (initialProductsSnapshotRef.current !== initialProductsSnapshotKey) {
		initialProductsSnapshotRef.current = initialProductsSnapshotKey
		initialRequestRef.current = {
			urlParamsKey,
			filterUrl: filterUrl || '',
			brandCode: brandCode || '',
			categoryCode: categoryCode || ''
		}
	}

	const isInitialRequestArgs = Boolean(
		canUseInitialForCurrentRoute &&
			initialRequestRef.current &&
			initialRequestRef.current.urlParamsKey === urlParamsKey &&
			initialRequestRef.current.filterUrl === (filterUrl || '') &&
			initialRequestRef.current.brandCode === (brandCode || '') &&
			initialRequestRef.current.categoryCode === (categoryCode || '')
	)

	const hasQueryItems = Boolean(initProductData?.ITEMS?.length)
	const initialTotal = initialProductsData?.TOTAL || 0
	const queryTotal = initProductData?.TOTAL || 0
	const shouldProtectInitialFromDowngrade = Boolean(initialProductsData?.ITEMS?.length && isInitialRequestArgs)
	const shouldUseQueryData = hasQueryItems && (!shouldProtectInitialFromDowngrade || queryTotal >= initialTotal)
	const effectiveProductData = shouldUseQueryData ? initProductData : initialProductsData

	useEffect(() => {
		setMoreData(null)
	}, [page, sort, filterUrl])

	const getMoreProducts = async (page: number) => {
		const urlParams = createCatalogBrandsUrlParams({ sort, page })

		const moreDataRes = await fetchMoreData(
			{
				params: urlParams,
				filterUrl,
				brandCode,
				categoryCode,
				regionCookies
			},
			true
		)

		setMoreData(moreDataRes?.data)
	}

	const products = useMemo(
		() => [...(effectiveProductData?.ITEMS || []), ...(moreData?.ITEMS || [])],
		[effectiveProductData, moreData]
	)

	const hasProducts = Boolean(effectiveProductData?.ITEMS?.length)

	return {
		products,
		currentPage: moreData?.CURRENT_PAGE || effectiveProductData?.CURRENT_PAGE || 0,
		lastPage: effectiveProductData?.LAST_PAGE || 0,
		totalProducts: effectiveProductData?.TOTAL || 0,
		loading: (isLazyProductLoading || isLazyProductFetching) && !hasProducts,
		getMoreProducts
	}
}
